// src/pages/About.jsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/autoplay';
import { Leaf, ChevronRight, Phone, Mail, MapPin } from 'lucide-react';

// === IMAGES ===
import heroBg from '../assets/Mahi/110.jpeg';
import slide1 from '../assets/Mahi/79.jpeg';
import slide2 from '../assets/Mahi/112.jpeg';
import slide3 from '../assets/Mahi/28.jpeg';
import slide4 from '../assets/Mahi/35.jpeg';
import slide5 from '../assets/Mahi/116.jpeg';
import historyImage from '../assets/Mahi/111.jpeg';
import chairmanImage from '../assets/Mahi/132.jpeg';
import staffImage from '../assets/Mahi/104.jpeg';
import contactBgImage from '../assets/Mahi/84.jpeg';

const heroSlides = [
    { image: slide1, titleKey: "slide1" },
    { image: slide2, titleKey: "slide2" },
    { image: slide3, titleKey: "slide3" },
    { image: slide4, titleKey: "slide4" },
    { image: slide5, titleKey: "slide5" },
];



const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
};
// === FRAMER MOTION VARIANTS ===


const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};
const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const About = () => {
    const { t } = useTranslation('about');

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-[#E0DAC0]">
            {/* ===== HERO SECTION - MOBILE/SMALL ===== */}
            <motion.section
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="md:hidden relative min-h-[100vh] flex items-center justify-center overflow-hidden"
            >
                {/* Background Image + Overlay */}
                <div className="absolute inset-0">
                    <img
                        src={heroBg}
                        alt="Muthumuni Beach Resort"
                        className="w-full h-full object-cover brightness-[0.65] scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
                </div>

                {/* Content Container - Centered */}
                <div className="relative z-10 container mx-auto px-5 text-center">
                    <motion.div variants={fadeInUp} className="space-y-6">
                        <div className="inline-flex items-center gap-3 justify-center">
                            <Leaf className="w-6 h-6 text-[#E8C07D]" />
                            <span className="text-[#E8C07D] uppercase tracking-wider text-xs font-medium">
                                Mahi Beach Ayurveda
                            </span>
                        </div>

                        <h1 className="text-3xl sm:text-4xl font-serif font-light text-white leading-tight drop-shadow-2xl">
                            <span className="block">{t('heroTitle')}</span>
                            <span className="block text-[#E8C07D] mt-3">{t('heroSubtitle')}</span>
                            <span className="block text-2xl sm:text-3xl mt-2 opacity-90">
                                {t('heroBeachResort')}
                            </span>
                        </h1>

                        <p className="text-base sm:text-lg text-white/85 font-light max-w-md mx-auto leading-relaxed">
                            {t('heroDescription')}
                        </p>

                        <div className="flex flex-col gap-4 mt-8 max-w-sm mx-auto">
                            <a
                                href="/search"
                                className="bg-[#E8C07D] text-[#1A2F2A] px-6 py-4 rounded-full font-medium text-base hover:bg-white transition-all shadow-xl flex items-center justify-center gap-2 group"
                            >
                                <span>{t('bookDeluxeRoom')}</span>
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>

                            <a
                                href="https://wa.me/+94766111678"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="border-2 border-white/70 text-white px-6 py-4 rounded-full font-medium text-base hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                            >
                                <Phone className="w-4 h-4" />
                                {t('contactUs')}
                            </a>
                        </div>

                        {/* Mini Gallery Slider - Mobile */}
                        <div className="mt-10">
                            <Swiper
                                modules={[Autoplay]}
                                slidesPerView={1.15}
                                spaceBetween={12}
                                centeredSlides={true}
                                loop
                                speed={700}
                                autoplay={{ delay: 3200, disableOnInteraction: false }}
                                className="h-[220px] sm:h-[280px] rounded-xl overflow-hidden shadow-2xl"
                            >
                                {heroSlides.map((slide, idx) => (
                                    <SwiperSlide key={idx}>
                                        <div className="relative h-full w-full rounded-xl overflow-hidden">
                                            <img
                                                src={slide.image}
                                                alt={t(slide.titleKey)}
                                                className="absolute inset-0 w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                            <p className="absolute bottom-4 left-4 text-white text-base sm:text-lg font-medium">
                                                {t(slide.titleKey)}
                                            </p>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </motion.div>
                </div>
            </motion.section>

            {/* ===== HERO SECTION - DESKTOP/LARGE ===== */}
            <motion.section
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="hidden md:flex relative min-h-[85vh] md:min-h-[100vh] items-end overflow-hidden"
            >
                {/* Background Image + Overlay */}
                <div className="absolute inset-0">
                    <img
                        src={heroBg}
                        alt="Muthumuni Beach Resort"
                        className="w-full h-full object-cover brightness-[0.72] scale-[1.03] transition-transform duration-[20s]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65" />
                </div>

                {/* Content Container */}
                <div className="relative z-10 container mx-auto px-5 sm:px-8 lg:px-12 pb-16 md:pb-24 lg:pb-32 max-w-7xl">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-end">
                        {/* Left - Main Headline & CTA */}
                        <motion.div variants={fadeInUp} className="space-y-8 md:space-y-10">
                            <div className="inline-flex items-center gap-4">
                                <Leaf className="w-8 h-8 md:w-10 md:h-10 text-[#E8C07D]" />
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                    Mahi beach Ayurveda
                                </span>
                            </div>

                            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-light text-white leading-tight drop-shadow-2xl">
                                <span className="block">{t('heroTitle')}</span>
                                <span className="block text-[#E8C07D] mt-2 md:mt-4">{t('heroSubtitle')}</span>
                                <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-2 opacity-90">
                                    {t('heroBeachResort')}
                                </span>
                            </h1>

                            <p className="text-lg md:text-xl lg:text-2xl text-white/90 font-light max-w-2xl">
                                {t('heroDescription')}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-5 mt-8 md:mt-10">
                                <a
                                    href="/search"
                                    className="bg-[#E8C07D] text-[#1A2F2A] px-8 py-5 rounded-full font-medium text-lg hover:bg-white transition-all shadow-xl flex items-center justify-center gap-2 group"
                                >
                                    <span>{t('bookDeluxeRoom')}</span>
                                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </a>

                                <a
                                    href="https://wa.me/+94766111678"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="border-2 border-white/70 text-white px-8 py-5 rounded-full font-medium text-lg hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                                >
                                    <Phone className="w-5 h-5" />
                                    {t('contactUs')}
                                </a>
                            </div>

                            {/* Decorative line */}
                            <div className="h-1 w-32 md:w-48 bg-gradient-to-r from-[#E8C07D] to-transparent rounded-full mt-10" />
                        </motion.div>

                        {/* Right - Mini Gallery Slider */}
                        <motion.div
                            variants={fadeInUp}
                            className="relative h-[420px] xl:h-[480px] w-full max-w-[520px] ml-auto"
                        >
                            <Swiper
                                modules={[Autoplay, Pagination]}
                                slidesPerView={1.15}
                                spaceBetween={20}
                                loop
                                speed={800}
                                autoplay={{ delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }}
                                pagination={{ clickable: true, dynamicBullets: true }}
                                className="h-full rounded-2xl overflow-hidden shadow-2xl"
                            >
                                {heroSlides.map((slide, idx) => (
                                    <SwiperSlide key={idx}>
                                        <div className="relative h-full w-full rounded-2xl overflow-hidden">
                                            <img
                                                src={slide.image}
                                                alt={t(slide.titleKey)}
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                            <div className="absolute bottom-6 left-6 right-6">
                                                <p className="text-white text-xl lg:text-2xl font-serif font-light">
                                                    {t(slide.titleKey)}
                                                </p>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* ===== HISTORY SECTION ===== */}
            <section className="py-20 md:py-28 lg:py-36 bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    {/* ─── MOBILE / TABLET LAYOUT ──────────────────────────────────────── */}
                    <div className="block lg:hidden space-y-12">
                        {/* Label + Title */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-6 text-center"
                        >
                            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4">
                                <div className="h-px w-16 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent flex-1 max-w-[80px]" />
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                    {t('historyLabel')}
                                </span>
                                <div className="h-px w-16 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent flex-1 max-w-[80px]" />
                            </motion.div>

                            <motion.h2
                                variants={fadeInUp}
                                className="text-4xl sm:text-5xl md:text-6xl font-serif font-light text-[#1A2F2A] leading-tight"
                            >
                                {t('historyTitle')}
                            </motion.h2>
                        </motion.div>

                        {/* Image with decorative frame */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                            className="relative rounded-3xl overflow-hidden shadow-2xl group aspect-[4/3] sm:aspect-[3/2]"
                        >
                            <img
                                src={historyImage}
                                alt="Muthumuni Ayurveda Beach Resort heritage"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                            {/* Floating heritage badge */}

                            {/* Elegant corner accents */}
                            <div className="absolute top-6 right-6 w-20 h-20 border-t-2 border-r-2 border-[#E8C07D]/70 rounded-tr-3xl" />
                            <div className="absolute bottom-6 left-6 w-20 h-20 border-b-2 border-l-2 border-[#E8C07D]/70 rounded-bl-3xl" />
                        </motion.div>

                        {/* Text + Stats + CTA */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-8 text-center sm:text-left"
                        >
                            <div className="space-y-5 text-[#2D3F3A]/90 text-lg sm:text-xl leading-relaxed">
                                <motion.p variants={fadeInUp}>{t('historyDesc1')}</motion.p>
                                <motion.p variants={fadeInUp} className="text-[#2D3F3A]/80">{t('historyDesc2')}</motion.p>
                                <motion.p variants={fadeInUp} className="text-[#2D3F3A]/80">{t('historyDesc3')}</motion.p>
                            </div>

                            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-8 pt-6">
                                <div className="text-center">
                                    <div className="text-5xl sm:text-6xl font-light text-[#E8C07D]">{t('statYear')}</div>
                                    <p className="mt-2 text-sm sm:text-base text-[#2D3F3A]/70">{t('statYearLabel')}</p>
                                </div>
                                <div className="text-center">
                                    <div className="text-5xl sm:text-6xl font-light text-[#E8C07D]">{t('statGuest')}</div>
                                    <p className="mt-2 text-sm sm:text-base text-[#2D3F3A]/70">{t('statGuestLabel')}</p>
                                </div>
                            </motion.div>

                            <motion.div
                                variants={fadeInUp}
                                className="flex flex-col sm:flex-row gap-5 justify-center pt-8"
                            >
                                <a
                                    href="/rooms"
                                    className="bg-[#1A2F2A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg flex items-center justify-center gap-2 group"
                                >
                                    <span>{t('experienceLegacy')}</span>
                                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </a>
                                <a
                                    href="/contact-us"
                                    className="border-2 border-[#E8C07D] text-[#E8C07D] px-8 py-4 rounded-full font-medium hover:bg-[#E8C07D]/10 transition-all"
                                >
                                    {t('contactUs')}
                                </a>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* ─── DESKTOP / LG+ LAYOUT ────────────────────────────────────────── */}
                    <div className="hidden lg:grid lg:grid-cols-2 gap-16 xl:gap-24 items-center">
                        {/* Left - Text & Stats */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInLeft}
                            className="space-y-10"
                        >
                            <div className="flex items-center gap-5">
                                <div className="h-px w-20 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                                <span className="text-[#E8C07D] uppercase tracking-widest text-base font-medium">
                                    {t('historyLabel')}
                                </span>
                                <div className="h-px w-20 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                            </div>

                            <h2 className="text-6xl xl:text-7xl font-serif font-light text-[#1A2F2A] leading-tight">
                                {t('historyTitle')}
                            </h2>

                            <div className="space-y-6 text-[#2D3F3A]/90 text-xl leading-relaxed">
                                <p>{t('historyDesc1')}</p>
                                <p className="text-[#2D3F3A]/80">{t('historyDesc2')}</p>
                                <p className="text-[#2D3F3A]/80">{t('historyDesc3')}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-12 pt-8">
                                <div>
                                    <div className="text-7xl font-light text-[#E8C07D]">{t('statYear')}</div>
                                    <p className="mt-3 text-lg text-[#2D3F3A]/70">{t('statYearLabel')}</p>
                                </div>
                                <div>
                                    <div className="text-7xl font-light text-[#E8C07D]">{t('statGuest')}</div>
                                    <p className="mt-3 text-lg text-[#2D3F3A]/70">{t('statGuestLabel')}</p>
                                </div>
                            </div>

                            <div className="flex gap-6 pt-10">
                                <a
                                    href="/rooms"
                                    className="bg-[#1A2F2A] text-white px-10 py-5 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-xl flex items-center gap-2 group text-lg"
                                >
                                    <span>{t('experienceLegacy')}</span>
                                    <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                                </a>
                                <a
                                    href="/contact-us"
                                    className="border-2 border-[#E8C07D] text-[#E8C07D] px-10 py-5 rounded-full font-medium hover:bg-[#E8C07D]/10 transition-all text-lg"
                                >
                                    {t('contactUs')}
                                </a>
                            </div>
                        </motion.div>

                        {/* Right - Large Image with floating card */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInRight}
                            className="relative rounded-3xl overflow-hidden shadow-2xl group aspect-[4/5] xl:aspect-[5/6]"
                        >
                            <img
                                src={historyImage}
                                alt="Heritage of Muthumuni Ayurveda Beach Resort"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />

                            {/* Large floating heritage card */}


                            {/* Decorative corners */}
                            <div className="absolute top-10 right-10 w-24 h-24 border-t-4 border-r-4 border-[#E8C07D]/60 rounded-tr-3xl" />
                            <div className="absolute bottom-10 left-10 w-24 h-24 border-b-4 border-l-4 border-[#E8C07D]/60 rounded-bl-3xl" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ===== CHAIRMAN'S VISION SECTION ===== */}
            <section className="py-20 md:py-28 lg:py-36 bg-gradient-to-b from-[#F2EEE5] to-[#F8F5ED]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12">
                    {/* ─── MOBILE / TABLET LAYOUT ──────────────────────────────────────── */}
                    <div className="block lg:hidden space-y-12">
                        {/* Label + Title */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-6 text-center"
                        >
                            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4">
                                <div className="h-px w-16 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent flex-1 max-w-[80px]" />
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                    {t('chairmanLabel')}
                                </span>
                                <div className="h-px w-16 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent flex-1 max-w-[80px]" />
                            </motion.div>

                            <motion.h2
                                variants={fadeInUp}
                                className="text-4xl sm:text-5xl md:text-6xl font-serif font-light text-[#1A2F2A] leading-tight"
                            >
                                {t('chairmanTitle')}
                            </motion.h2>
                        </motion.div>

                        {/* Chairman Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                            className="relative rounded-3xl overflow-hidden shadow-2xl group aspect-[4/3] sm:aspect-[3/2]"
                        >
                            <img
                                src={chairmanImage}
                                alt={t('chairmanImageAlt')}
                                className="absolute inset-0 w-full h-[400px] object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                            {/* Floating chairman badge */}
                            <div className="absolute bottom-6 left-6 bg-[#E8C07D]/95 backdrop-blur-sm px-6 py-3 rounded-full">
                                <p className="text-[#1A2F2A] font-medium text-sm">{t('chairmanName')}</p>
                            </div>

                            {/* Elegant corner accents */}
                            <div className="absolute top-6 right-6 w-20 h-20 border-t-2 border-r-2 border-[#E8C07D]/70 rounded-tr-3xl" />
                            <div className="absolute bottom-6 left-6 w-20 h-20 border-b-2 border-l-2 border-[#E8C07D]/70 rounded-bl-3xl" />
                        </motion.div>

                        {/* Text + Signature */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-8 text-center sm:text-left"
                        >
                            <div className="space-y-5 text-[#2D3F3A]/90 text-lg sm:text-xl leading-relaxed">
                                <motion.p variants={fadeInUp}>{t('chairmanDesc1')}</motion.p>
                                <motion.p variants={fadeInUp} className="text-[#2D3F3A]/80">{t('chairmanDesc2')}</motion.p>
                                <motion.p variants={fadeInUp} className="text-[#2D3F3A]/80">{t('chairmanDesc3')}</motion.p>
                            </div>

                            <motion.div variants={fadeInUp} className="pt-6 border-t border-[#E8C07D]/20">
                                <p className="text-2xl font-serif font-light text-[#1A2F2A] mb-2">
                                    {t('chairmanSignature')}
                                </p>
                                <p className="text-sm text-[#2D3F3A]/70 uppercase tracking-wider">
                                    {t('chairmanBadge')}
                                </p>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* ─── DESKTOP / LG+ LAYOUT ────────────────────────────────────────── */}
                    <div className="hidden lg:grid lg:grid-cols-2 gap-16 xl:gap-24 items-center">
                        {/* Left - Chairman Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInLeft}
                            className="relative rounded-3xl overflow-hidden shadow-2xl group aspect-[4/5] xl:aspect-[5/6]"
                        >
                            <img
                                src={chairmanImage}
                                alt={t('chairmanImageAlt')}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />

                            {/* Large floating chairman card */}
                            <div className="absolute bottom-10 left-10 right-10 bg-white/95 backdrop-blur-md p-6 rounded-2xl shadow-xl">
                                <p className="text-xl font-serif font-light text-[#1A2F2A] mb-2">
                                    {t('chairmanName')}
                                </p>
                                <p className="text-sm text-[#E8C07D] uppercase tracking-wider font-medium">
                                    {t('chairmanBadge')}
                                </p>
                            </div>

                            {/* Decorative corners */}
                            <div className="absolute top-10 right-10 w-24 h-24 border-t-4 border-r-4 border-[#E8C07D]/60 rounded-tr-3xl" />
                            <div className="absolute bottom-10 left-10 w-24 h-24 border-b-4 border-l-4 border-[#E8C07D]/60 rounded-bl-3xl" />
                        </motion.div>

                        {/* Right - Text & Message */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInRight}
                            className="space-y-10"
                        >
                            <div className="flex items-center gap-5">
                                <div className="h-px w-20 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                                <span className="text-[#E8C07D] uppercase tracking-widest text-base font-medium">
                                    {t('chairmanLabel')}
                                </span>
                                <div className="h-px w-20 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                            </div>

                            <h2 className="text-6xl xl:text-7xl font-serif font-light text-[#1A2F2A] leading-tight">
                                {t('chairmanTitle')}
                            </h2>

                            <div className="space-y-6 text-[#2D3F3A]/90 text-xl leading-relaxed">
                                <p>{t('chairmanDesc1')}</p>
                                <p className="text-[#2D3F3A]/80">{t('chairmanDesc2')}</p>
                                <p className="text-[#2D3F3A]/80">{t('chairmanDesc3')}</p>
                            </div>

                            <div className="pt-10 border-t border-[#E8C07D]/20">
                                <p className="text-3xl font-serif font-light text-[#1A2F2A] mb-3">
                                    {t('chairmanSignature')}
                                </p>
                                <p className="text-base text-[#2D3F3A]/70 uppercase tracking-wider">
                                    {t('chairmanBadge')}
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>



            {/* ===== OUR STAFF SECTION ===== */}
            <section className="py-20 md:py-28 lg:py-36 bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    {/* ─── MOBILE / TABLET LAYOUT (stacked) ──────────────────────────────── */}
                    <div className="block lg:hidden space-y-12">
                        {/* Label + Title */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="text-center space-y-6"
                        >
                            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-5">
                                <div className="h-px flex-1 max-w-[70px] bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                    {t('staffLabel')}
                                </span>
                                <div className="h-px flex-1 max-w-[70px] bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                            </motion.div>

                            <motion.h2
                                variants={fadeInUp}
                                className="text-4xl sm:text-5xl md:text-6xl font-serif font-light text-[#1A2F2A] leading-tight"
                            >
                                {t('staffTitle')}
                            </motion.h2>
                        </motion.div>

                        {/* Image with elegant frame & badge */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                            className="relative rounded-3xl overflow-hidden shadow-2xl group aspect-[4/3] sm:aspect-[3/2]"
                        >
                            <img
                                src={staffImage}
                                alt={t('staffImageAlt')}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent pointer-events-none" />

                            {/* Floating team commitment badge */}

                            {/* Subtle corner decorations */}
                            <div className="absolute top-8 right-8 w-20 h-20 border-t-2 border-r-2 border-[#E8C07D]/60 rounded-tr-3xl" />
                            <div className="absolute bottom-8 left-8 w-20 h-20 border-b-2 border-l-2 border-[#E8C07D]/60 rounded-bl-3xl" />
                        </motion.div>

                        {/* Text + Stats + CTA */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-10 text-center"
                        >
                            <div className="space-y-6 text-lg sm:text-xl leading-relaxed text-[#2D3F3A]/90">
                                <motion.p variants={fadeInUp}>{t('staffDesc1')}</motion.p>
                                <motion.p variants={fadeInUp} className="text-[#2D3F3A]/80">{t('staffDesc2')}</motion.p>
                                <motion.p variants={fadeInUp} className="text-[#2D3F3A]/80">{t('staffDesc3')}</motion.p>
                            </div>

                            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-8 pt-6">
                                <div>
                                    <div className="text-5xl sm:text-6xl font-light text-[#E8C07D]">{t('statStaff')}</div>
                                    <p className="mt-2 text-sm sm:text-base text-[#2D3F3A]/70">{t('statStaffLabel')}</p>
                                </div>
                                <div>
                                    <div className="text-5xl sm:text-6xl font-light text-[#E8C07D]">{t('statSatisfaction')}</div>
                                    <p className="mt-2 text-sm sm:text-base text-[#2D3F3A]/70">{t('statSatisfactionLabel')}</p>
                                </div>
                            </motion.div>

                            <motion.div variants={fadeInUp} className="pt-8">
                                <a
                                    href="/contact-us"
                                    className="inline-flex items-center gap-3 bg-[#1A2F2A] text-white px-8 py-5 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg text-lg"
                                >
                                    {t('contactUs')}
                                    <span>→</span>
                                </a>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* ─── DESKTOP / LG+ LAYOUT (side-by-side) ───────────────────────────── */}
                    <div className="hidden lg:grid lg:grid-cols-2 gap-16 xl:gap-24 items-center">
                        {/* Left – Text content */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInLeft}
                            className="space-y-12"
                        >
                            <div className="flex items-center gap-6">
                                <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                                <span className="text-[#E8C07D] uppercase tracking-widest text-base font-medium">
                                    {t('staffLabel')}
                                </span>
                                <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#E8C07D] to-transparent" />
                            </div>

                            <h2 className="text-6xl xl:text-7xl font-serif font-light text-[#1A2F2A] leading-tight">
                                {t('staffTitle')}
                            </h2>

                            <div className="space-y-7 text-xl leading-relaxed text-[#2D3F3A]/90">
                                <p>{t('staffDesc1')}</p>
                                <p className="text-[#2D3F3A]/80">{t('staffDesc2')}</p>
                                <p className="text-[#2D3F3A]/80">{t('staffDesc3')}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-16 pt-8">
                                <div>
                                    <div className="text-7xl font-light text-[#E8C07D]">{t('statStaff')}</div>
                                    <p className="mt-3 text-lg text-[#2D3F3A]/70">{t('statStaffLabel')}</p>
                                </div>
                                <div>
                                    <div className="text-7xl font-light text-[#E8C07D]">{t('statSatisfaction')}</div>
                                    <p className="mt-3 text-lg text-[#2D3F3A]/70">{t('statSatisfactionLabel')}</p>
                                </div>
                            </div>

                            <div className="pt-10">
                                <a
                                    href="/contact-us"
                                    className="inline-flex items-center gap-3 bg-[#1A2F2A] text-white px-10 py-5 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-xl text-lg"
                                >
                                    {t('contactUs')}
                                    <span>→</span>
                                </a>
                            </div>
                        </motion.div>

                        {/* Right – Large hero-style image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInRight}
                            className="relative rounded-3xl overflow-hidden shadow-2xl group aspect-[4/5] xl:aspect-[5/6]"
                        >
                            <img
                                src={staffImage}
                                alt={t('staffImageAlt')}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent pointer-events-none" />

                            {/* Large floating badge */}


                            {/* Elegant corner accents */}
                            <div className="absolute top-12 right-12 w-28 h-28 border-t-4 border-r-4 border-[#E8C07D]/50 rounded-tr-3xl" />
                            <div className="absolute bottom-12 left-12 w-28 h-28 border-b-4 border-l-4 border-[#E8C07D]/50 rounded-bl-3xl" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ===== CONTACT CTA SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
                className="relative py-24 md:py-32 lg:py-40 overflow-hidden mb-10"
            >
                {/* Background with sophisticated gradient overlay */}
                <div className="absolute inset-0">
                    <img
                        src={contactBgImage}
                        alt="Contact Muthumuni Resort"
                        className="w-full h-full object-cover brightness-[0.45] contrast-[1.15] scale-[1.03]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/65 to-black/80" />
                    {/* Subtle gold accent overlay */}
                    <div className="absolute inset-0 bg-gradient-to-tl from-[#E8C07D]/8 via-transparent to-transparent pointer-events-none" />
                </div>

                <div className="relative container mx-auto px-5 sm:px-8 lg:px-12 max-w-6xl text-center">
                    {/* Label */}
                    <motion.div
                        variants={fadeInUp}
                        className="flex items-center justify-center gap-6 mb-8 md:mb-10"
                    >
                        <div className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent via-[#E8C07D]/70 to-transparent" />
                        <span className="text-[#E8C07D] uppercase tracking-[0.3em] text-sm md:text-base font-medium">
                            {t('ctaLabel')}
                        </span>
                        <div className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent via-[#E8C07D]/70 to-transparent" />
                    </motion.div>

                    {/* Main Heading */}
                    <motion.h2
                        variants={fadeInUp}
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-light text-white leading-tight mb-6 md:mb-8 max-w-5xl mx-auto"
                    >
                        {t('ctaHeading')}
                    </motion.h2>

                    {/* Description */}
                    <motion.p
                        variants={fadeInUp}
                        className="text-lg sm:text-xl md:text-2xl text-white/90 font-light leading-relaxed max-w-3xl mx-auto mb-12 md:mb-16"
                    >
                        {t('ctaDesc')}
                    </motion.p>

                    {/* CTA Buttons */}
                    <motion.div
                        variants={fadeInUp}
                        className="flex flex-col sm:flex-row items-center justify-center gap-5 md:gap-8 mb-16 md:mb-20"
                    >
                        <a
                            href="/contact-us"
                            className="group relative inline-flex items-center justify-center gap-3 bg-[#E8C07D] text-[#1A2F2A] px-10 py-5 rounded-full font-medium text-lg hover:bg-white transition-all duration-400 shadow-xl hover:shadow-2xl min-w-[240px]"
                        >
                            <Phone className="w-5 h-5" />
                            <span>{t('ctaButtonContact')}</span>
                            <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </a>

                        <a
                            href="/search"
                            className="group inline-flex items-center justify-center gap-3 border-2 border-[#E8C07D]/80 text-[#E8C07D] px-10 py-5 rounded-full font-medium text-lg hover:bg-[#E8C07D]/10 transition-all duration-400 min-w-[240px]"
                        >
                            <span>{t('bookDeluxeRoomShort')}</span>
                            <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </a>
                    </motion.div>

                    {/* Quick Contact Info Cards */}
                    <motion.div
                        variants={staggerContainer}
                        className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto"
                    >
                        {[
                            {
                                icon: Phone,
                                label: t('quickInfoCall'),
                                value: "+94 76 611 1678",
                                href: "tel:+94766111678",
                            },
                            {
                                icon: Mail,
                                label: t('quickInfoEmail'),
                                value: "mahibeach23@gmail.com",
                                href: "mailto:mahibeach23@gmail.com",
                            },
                            {
                                icon: MapPin,
                                label: t('quickInfoLocation'),
                                value: t('locationCity'),
                                href: "#", // can link to map if desired
                            },
                        ].map((item, index) => (
                            <motion.a
                                key={index}
                                href={item.href}
                                variants={fadeInUp}
                                className="group flex flex-col items-center gap-4 p-6 md:p-8 rounded-2xl bg-white/10 backdrop-blur-lg border border-white/15 hover:border-[#E8C07D]/40 transition-all duration-400 hover:shadow-xl hover:shadow-[#E8C07D]/10"
                            >
                                <div className="w-14 h-14 rounded-full bg-[#E8C07D]/20 flex items-center justify-center group-hover:bg-[#E8C07D]/40 transition-colors">
                                    <item.icon className="w-7 h-7 text-[#E8C07D]" />
                                </div>
                                <div className="text-center">
                                    <p className="text-sm uppercase tracking-wider text-white/80 font-medium mb-1">
                                        {item.label}
                                    </p>
                                    <p className="text-white text-base md:text-lg font-medium">
                                        {item.value}
                                    </p>
                                </div>
                            </motion.a>
                        ))}
                    </motion.div>
                </div>
            </motion.section>
        </div>
    );
};

export default About;